import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  Modal,
  Stepper,
  Group,
  Button,
  TextInput,
  Textarea,
  Select,
  Text,
  Divider,
  Card,
  Grid,
  Center,
  Box,
  NumberInput,
} from "@mantine/core";
import { useForm } from '@mantine/form'
import { IconPlus, IconChevronLeft, IconGrid4x4, IconGridDots, IconChevronRight, IconDeviceFloppy, } from "@tabler/icons-react";
import CriteriaCardEditor from "./CriteriaCardEditor";
import KpiCardEditor from './KpiCardEditor';
import textBrief from '../../utils/textBrief'
import { notifyError } from "../Toast";

export default function CreateMatrixModal({
  opened,
  onSave,
  onClose,
  objectives = [],
  matrix = null,
}) 
{
  const {user} = useSelector((state) => state.auth)
  const {themes, categories, saving} = useSelector((state) => state.matrix)

  const [activeStep, setActiveStep] = useState(0);
  const [loadingFull, setLoadingFull] = useState(false);
  const [criteria, setCriteria] = useState([]);
  const [kpis, setKpis] = useState([]);

  const matrixId = matrix?.Id ?? null;
  
  const formMatrix = useForm({
    initialValues: {
      name: matrix?.name || "",
      themeId: matrix?.themeId || null,
      version: matrix?.version || "",
      description: matrix?.description || "",
    },
    validate: {
      name: (v) => (v?.trim() ? null : "Requerido"),
      themeId: (v) => (v ? null : "Requerido"),
      version: (v) => (/^\d+(\.\d+)?$/.test(v) ? null : "Requerido"),
      description: (v) => (v?.trim() ? null : "Requerido"),
    },
  });

  const objectiveOptions = useMemo(
    () => (objectives || []).map((o) => ({ value: o.Id, label: `${o.Number}. ${textBrief(o.Description, 40) || "—"}`,})),
    [objectives]
  );
  const themeOptions = useMemo(
    () => (themes.data || []).map((o) => ({ value: o.Id, label: o.descripcion })),
    [themes.data]
  );
  const categoryOptions = useMemo(
    () => (categories.data || []).map((o) => ({ value: o.Id, label: o.nombre })),
    [categories.data]
  );

  const hydrateFromFull = (full) => {
    // form
    formMatrix.setValues({
      name: full?.nombre || "",
      themeId: full?.tematica.Id,
      version: full?.version || "",
      description: full?.descripcion || "",
    });

    // criterios
    const crits = (full?.criterios || [])
      .slice()
      .sort((a, b) => (a.numCriterio || 0) - (b.numCriterio || 0))
      .map((c) => {
        const cid = c.Id || crypto.randomUUID?.() || String(Date.now());
        return {
          id: c.Id ?? null,       // id real si existe
          cid,                    // id cliente para relacionar KPIs/criterio
          order: c.numCriterio,
          description: c.descripcion || "",
          categories: (c.categorias || []).map((x) => x.Id),
          objectiveId: c.IdObjetivo ?? null,
        };
      });

    setCriteria(crits);

    // map para linkear kpis → criterio por cid
    const cidByAny = new Map(crits.map((c) => [c.id ?? c.cid, c.cid]));

    const flatKpis = [];
    for (const c of full?.criterios || []) {
      const linkCid = cidByAny.get(c.Id) || null;
      for (const k of c.kpiList || []) {
        flatKpis.push({
          id: k.Id ?? null,
          cid: k.Id || crypto.randomUUID?.() || String(Date.now()),
          number: k.numKpi,
          name: k.titulo || "",
          description: k.descripcion || "",
          category: k.categoria || null,
          type: k.tipo || null,
          helpText: k.ayuda || "",
          example: k.ejemplo || "",
          criterionId: linkCid, // importantísimo
        });
      }
    }
    flatKpis.sort((a, b) => (a.number || 0) - (b.number || 0));
    setKpis(flatKpis);
  };

  useEffect(() => {
    if (!opened) return;

    if (!matrixId) {
      setLoadingFull(false);
      formMatrix.reset();
      setCriteria([]);
      setKpis([]);
      return;
    }

    const ac = new AbortController();
    (async () => {
      try {
        setLoadingFull(true);

        const resp = await fetch(`${process.env.REACT_APP_API_BASE_URL}reportes/matrices/${matrixId}/full`, {
          method: "GET",
          headers: {
            'Content-Type': 'application/json',
            'Authorization': 'Bearer ' + user.token
          },
          signal: ac.signal,
        });

        if (!resp.ok) {
          const msg = await resp.text().catch(() => "");
          throw new Error(msg || `HTTP ${resp.status}`);
        }

        const full = await resp.json();
        hydrateFromFull(full);
      } catch (e) {
        if (e.name !== "AbortError") notifyError(e.message || String(e));
      } finally {
        setLoadingFull(false);
      }
    })();

    return () => ac.abort();
  }, [opened, matrixId]);

  const handleSave = () => {

    const kpisByCriterion = new Map();

    (kpis || []).forEach(k => {
      const key = k.criterionId;
      if (!kpisByCriterion.has(key)) kpisByCriterion.set(key, []);
      kpisByCriterion.get(key).push(k);
    }); 

    const criteriaOrdered = [...(criteria || [])]
      .sort((a,b)=> (a.order||0)-(b.order||0))
      .map(c => ({
        Id: c.id ?? null,
        IdObjetivo: c.objectiveId,
        numCriterio: c.order,
        descripcion: c.description,
        categorias: (c.categories || []).map((x) => ({ Id: x })),
        kpiList: (kpisByCriterion.get(c.cid || c.id) || [])
          .sort((a,b)=> (a.number||0)-(b.number||0))
          .map(k => ({
            Id: k.id ?? null,
            numKpi: k.number,
            titulo: k.name,
            descripcion: k.description,
            categoria: k.category,
            tipo: k.type,
            ayuda: k.helpText || null,
            ejemplo: k.example || null,
          })),
      }));  

    const payload = {
      Id: matrix?.id ?? null,
      tematica: {Id: formMatrix.values.themeId},
      version: formMatrix.values.version,
      nombre: formMatrix.values.name,
      descripcion: formMatrix.values.description,
      criterios: criteriaOrdered,
    };  
    onSave(payload);
  };

  const handleClose = () => {
    if (!saving && !loadingFull) onClose();
  }

  const LAST_STEP = 3;
  const handleNext = () => {
    if (activeStep === 0) {
      const res = formMatrix.validate();
      if (res.hasErrors) return;
    }
    if (activeStep === 1) {
      if (!criteria || criteria.length === 0) {
        notifyError('Debe de cargar al menos un criterio')
        return;
      }
    }
    if (activeStep === 2) {
      if (!kpis || kpis.length === 0) {
        notifyError('Debe de cargar al menos un kpi')
        return;
      }
    }
    setActiveStep((s) => Math.min(s + 1, LAST_STEP));
  };
  const handlePrev = () => setActiveStep((s) => Math.max(s - 1, 0));
  const busy = loadingFull || saving;

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      closeOnClickOutside={false}
      size="xl"
      radius="md"
      centered
      overlayProps={{
        backgroundOpacity: 0.55,
        blur: 1,
      }}
      title={<Text fw={600} size="xl">{matrixId ? "Editar Matriz" : "Crear Matriz"}</Text>}
    >
      <Stepper
        size="sm"
        active={activeStep}
        onStepClick={setActiveStep}
        allowNextStepsSelect={false}
        mt="md"
      >
        <Stepper.Step label="Matriz" description='Informacion general' icon={<IconGridDots size={18} />}>
          <Box px="md" py="sm">
            <Grid>
              <Grid.Col span={{ base: 12, md: 5 }}>
                <TextInput
                  label="Nombre de la Matriz"
                  placeholder="Ingrese un nombre"
                  {...formMatrix.getInputProps('name')}
                />
              </Grid.Col>

              <Grid.Col span={{ base: 12, md: 4 }}>
                <Select
                  label="Tematica"
                  placeholder="Seleccionar tematica..."
                  data={themeOptions}
                  searchable
                  clearable
                  {...formMatrix.getInputProps('themeId')}
                />
              </Grid.Col>

              <Grid.Col span={{ base: 12, md: 3 }}>
                <NumberInput
                  label="Version"
                  placeholder="Ej: 1.1"
                  decimalScale={1}
                  allowNegative={false}
                  {...formMatrix.getInputProps('version')}
                />
              </Grid.Col>

              <Grid.Col span={{ base: 12 }}>
                <Textarea
                  label="Descripción"
                  placeholder="Ingrese una descripción para la matriz..."
                  autosize
                  minRows={2}
                  {...formMatrix.getInputProps('description')}
                />
              </Grid.Col>
            </Grid>
          </Box>
        </Stepper.Step>

        <Stepper.Step label="Criterios" description='Definicion de Criterios' icon={<IconGridDots size={18}/>}>
          <CriteriaStep
            criteria={criteria}
            setCriteria={setCriteria}
            objectiveOptions={objectiveOptions}
            categoryOptions={categoryOptions}
            kpis={kpis}
          />
        </Stepper.Step>

        <Stepper.Step label="Indicadores" description="Definicion de KPIs" icon={<IconGrid4x4 size={18}/>}>
          <KpisStep
            kpis={kpis}
            setKpis={setKpis}
            criteria={criteria}
          />
        </Stepper.Step>

        <Stepper.Completed>
          <CompletedSummary
            matrixValues={formMatrix.values}
            criteria={criteria}
            kpis={kpis}
            objectiveOptions={objectiveOptions}
            themeOptions={themeOptions}
          />
        </Stepper.Completed>

      </Stepper>
      <Divider mt="md" mb="sm" />
      <Group justify="flex-end" align="center" mt="md" wrap="nowrap">
        <Button
          variant="default"
          leftSection={<IconChevronLeft size={16} />}
          disabled={activeStep === 0 || busy}
          onClick={handlePrev}
        >
          Atrás
        </Button>
        {activeStep < LAST_STEP ? (
          <Button
            variant="default"
            rightSection={<IconChevronRight size={16} />}
            onClick={handleNext}
            disabled={busy}
          >
            Siguiente
          </Button>
        ):(
          <Button
            onClick={handleSave}
            leftSection={<IconDeviceFloppy size={16}/>}
            loading={saving}
            disabled={busy}
          >
            Guardar Matriz
          </Button>
        )}
      </Group>
    </Modal>
  );
}

function CriteriaStep({
  criteria,
  setCriteria,
  objectiveOptions = [],
  categoryOptions = [],
  kpis = []
}) {

  const [editingId, setEditingId] = useState(null);

  const usedCriteria = useMemo(() => new Set((kpis || []).map(k => k.criterionId)), [kpis]);

  const handleAdd = () => {
    if (editingId) return;
    const cid = crypto.randomUUID?.() || String(Date.now());
    const nextOrder = (criteria?.length || 0) + 1;
    const item = {
      id: null,
      cid: cid,
      order: nextOrder,
      description: "",
      categories: [],
      objectiveId: null,
    };
    setCriteria((prev) => [...(prev || []), item]);
    setEditingId(cid);
  };

  const handleEdit = (cid) => setEditingId(cid);
  const handleCancel = () => setEditingId(null);

  const handleDelete = (cid) => {
    if (usedCriteria.has(cid)) {
      notifyError("No se puede eliminar el criterio porque tiene KPIs asociados.");
      return;
    }

    const filtered = (criteria || []).filter(c => c.cid !== cid);
    const resequenced = filtered
      .sort((a, b) => (Number(a.order) || 0) - (Number(b.order) || 0))
      .map((c, idx) => ({ ...c, order: idx + 1 }));
    
    setCriteria(resequenced);
      if (editingId === cid) setEditingId(null);
  };

  const saveCriterion = (list, updated) => {
    const arr = [...(list || [])];
    const n = parseInt(updated.order, 10);
    if (!Number.isInteger(n) || n < 1) return { error: "El número debe ser un entero ≥ 1", list: arr };
    if (!updated.description?.trim()) return { error: "El nombre es obligatorio", list: arr };
    if (!updated.objectiveId) return { error: "Debés vincular un objetivo", list: arr };

    const from = arr.findIndex(x => x.cid === updated.cid);
    if (from === -1) return { error: "Criterio no encontrado", list: arr };

    arr[from] = { ...arr[from], ...updated, order: n };

    const max = arr.length;
    const to = Math.min(Math.max(n, 1), max) - 1;
    const [item] = arr.splice(from, 1);
    arr.splice(to, 0, item);

    for (let i = 0; i < arr.length; i++) arr[i] = { ...arr[i], order: i + 1 };
    return { list: arr };
  };

  const handleSave = (updated) => {
    const { list, error } = saveCriterion(criteria, updated);
    if (error) { notifyError(error); return; }
    setCriteria(list);
    setEditingId(null);
  };

  const hasItems = criteria?.length > 0;

  return (
    <Box px="md" py="sm">

      {hasItems && (
        criteria.map((c, index) => (
          <Box key={index} mb="sm">
            <CriteriaCardEditor
              criterion={c}
              objectivesOptions={objectiveOptions}
              categoryOptions={categoryOptions}
              isEditing={editingId === (c.cid || c.id)}
              onEdit={handleEdit}
              onCancel={handleCancel}
              onSave={handleSave}
              onDelete={handleDelete}
            />
          </Box>
        ))
      )}

      <Card
        title={editingId ? "Terminá de editar el criterio actual" : "Agregar un nuevo criterio"}
        bg="gray.0" radius="md" p="lg" bd="dashed 2px gray.4" withBorder
        style={{
          opacity: editingId ? 0.6 : 1,
          cursor: editingId ? "not-allowed" : "pointer",
          transition: "background 120ms ease",
        }}
        onClick={() => !editingId && handleAdd()}
        onKeyDown={(e) => {
          if (!editingId && (e.key === "Enter" || e.key === " ")) {
            e.preventDefault();
            handleAdd();
          }
        }}
      >
        <Center>
          <Group gap="xs" align="center" wrap="nowrap">
            <IconPlus size={20} color="gray"/>
            <Text fw={600} c={'dimmed'}>Agregar Criterio</Text>
          </Group>
        </Center>
        {!hasItems && (<Text c="dimmed" size="sm" ta={'center'}> No se han definido criterios.</Text>)}
      </Card>
    </Box>
  );
}

function KpisStep({
  kpis,
  setKpis,
  criteria = [],
}) {
  const [editingId, setEditingId] = useState(null);
  const [saveError, setSaveError] = useState(null);

  const categoryOptions = ['Proceso', 'Resultado']
  const typeOptions = ['Cuantitativo y Cualitativo', 'Cuantitativo', 'Cualitativo']
  const criterionOptions = useMemo(
    () => (criteria || [])
      .slice()
      .sort((a, b) => (a.order || 0) - (b.order || 0))
      .map((c) => ({
        value: c.cid,
        label: `${c.order}. ${c.description || "Criterio"}`,
      })),
    [criteria]
  );

  const saveKpi = (list, updated) => {
    const arr = [...(list || [])];

    const n = parseInt(updated.number, 10);
    if (!Number.isInteger(n) || n < 1) return { error: "El número debe ser un entero ≥ 1", list: arr };
    if (!updated.name || !updated.name.trim()) return { error: "El nombre es obligatorio", list: arr };
    if (!updated.criterionId) return { error: "Debés vincular un criterio", list: arr };

    const from = arr.findIndex(x => x.cid === updated.cid);
    if (from === -1) return { error: "KPI no encontrado", list: arr };

    arr[from] = { ...arr[from], ...updated, number: n };

    const max = arr.length;
    const to = Math.min(Math.max(n, 1), max) - 1;
    const [item] = arr.splice(from, 1);
    arr.splice(to, 0, item);
    for (let i = 0; i < arr.length; i++) arr[i] = { ...arr[i], number: i + 1 };

    return { list: arr };
  };

  const handleAdd = () => {
    if (editingId) return;
    const cid = crypto.randomUUID?.() || String(Date.now());
    setKpis(prev => {
      const nextNumber = (prev?.length || 0) + 1;
      return [
        ...(prev || []),
        {
          id: null,
          cid: cid,
          number: nextNumber,
          name: "",
          description: "",
          category: null,
          type: null,
          helpText: "",
          example: "",
          criterionId: criterionOptions[0]?.value || null,
        },
      ];
    });
    setEditingId(cid);
    setSaveError(null);
  };

  const handleEdit = (cid) => setEditingId(cid);
  const handleCancel = () => setEditingId(null);

  const handleDelete = (cid) => {
    setKpis(prev => {
      const arr = (prev || []).filter(x => x.cid !== cid);
      for (let i = 0; i < arr.length; i++) arr[i] = { ...arr[i], number: i + 1 };
      return arr;
    });
    if (editingId === cid) setEditingId(null);
    setSaveError(null);
  };

  const handleSave = (updated) => {
    const { list, error } = saveKpi(kpis, updated);
    if (error) { setSaveError(error); return; }
    setKpis(list);
    setEditingId(null);
    setSaveError(null);
  };

  const hasItems = kpis.length > 0;

  return (
    <Box px="md" py="sm">
      {saveError && <Text c="red" size="sm" mb="xs">{saveError}</Text>}

      {kpis.map((k,index) => (
        <Box key={index} mb="sm">
          <KpiCardEditor
            kpi={k}
            isEditing={editingId === (k.cid || k.id)}
            kpiCategoryOptions={categoryOptions}
            kpiTypeOptions={typeOptions}
            criterionOptions={criterionOptions}
            onEdit={handleEdit}
            onCancel={handleCancel}
            onSave={handleSave}
            onDelete={handleDelete}
          />
        </Box>
      ))}

      <Card
        title={editingId ? "Terminá de editar el KPI actual" : "Agregar un nuevo KPI"}
        bg="gray.0" radius="md" p="lg" bd="dashed 2px gray.4" withBorder
        style={{
          opacity: editingId ? 0.6 : 1,
          cursor: editingId ? "not-allowed" : "pointer",
          transition: "background 120ms ease",
        }}
        onClick={() => !editingId && handleAdd()}
        onKeyDown={(e) => {
          if (!editingId && (e.key === "Enter" || e.key === " ")) {
            e.preventDefault();
            handleAdd();
          }
        }}
      >
        <Center>
          <Group gap="xs" align="center" wrap="nowrap">
            <IconPlus size={20} color="gray" />
            <Text fw={600} c="dimmed">Agregar KPI</Text>
          </Group>
        </Center>
        {!hasItems && (<Text c="dimmed" size="sm" ta="center">No se han definido indicadores.</Text>)}
      </Card>
    </Box>
  );
}

function CompletedSummary({ matrixValues, criteria=[], kpis=[], objectiveOptions=[], themeOptions=[] }) {
  const themeLabel = themeOptions.find(t => t.value === matrixValues.themeId)?.label || "—";
  const objLabel = (id) => objectiveOptions.find(o => o.value === id)?.label || "—";

  // kpis por criterio (usando cid)
  const kpisByCriterion = useMemo(() => {
    const m = new Map();
    (kpis || []).forEach(k => {
      const key = k.criterionId; // es un cid
      if (!m.has(key)) m.set(key, []);
      m.get(key).push(k);
    });
    // ordenar cada lista
    for (const arr of m.values()) arr.sort((a,b)=> (a.number||0)-(b.number||0));
    return m;
  }, [kpis]);

  const critOrdered = [...(criteria||[])].sort((a,b)=> (a.order||0)-(b.order||0));

  return (
    <Box px="md" py="sm">
      <Card withBorder radius="md" mb="sm" p="md">
        <Text fw={700} mb={6}>Matriz</Text>
        <Text size="sm"><Text span c="dimmed">Nombre: </Text>{matrixValues.name}</Text>
        <Text size="sm"><Text span c="dimmed">Temática: </Text>{themeLabel}</Text>
        <Text size="sm"><Text span c="dimmed">Versión: </Text>{matrixValues.version}</Text>
        <Text size="sm" mt={6}><Text span c="dimmed">Descripción: </Text>{matrixValues.description || "—"}</Text>
      </Card>

      <Card withBorder radius="md" p="md">
        <Text fw={700} mb={6}>Criterios & KPIs</Text>
        {critOrdered.length === 0 ? (
          <Text size="sm" c="dimmed">No hay criterios.</Text>
        ) : critOrdered.map(c => {
          const klist = kpisByCriterion.get(c.cid || c.id) || [];
          return (
            <Box key={c.cid || c.id} mb="sm">
              <Text fw={600}>{c.order}. {c.name || "Sin nombre"}</Text>
              <Text size="xs" c="dimmed">Objetivo: {objLabel(c.objectiveId)}</Text>
              {klist.length === 0 ? (
                <Text size="sm" c="dimmed" mt={4}>— sin KPIs —</Text>
              ) : (
                <Box pl="sm" mt={4}>
                  {klist.map(k => (
                    <Text key={k.cid || k.id} size="sm">
                      <Text span fw={600}>{k.number}.</Text> {k.name || "Sin nombre"}
                    </Text>
                  ))}
                </Box>
              )}
            </Box>
          );
        })}
      </Card>
    </Box>
  );
}
